import { useEffect, useState } from "react";
import { api, currency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { FileText, Sparkles, Loader2, Download, GitBranch } from "lucide-react";

export default function ExecutiveReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [attribution, setAttribution] = useState(null);
  const { user } = useAuth();
  const writable = can(user?.role, "report:generate");

  useEffect(() => {
    api.get("/attribution").then((r) => setAttribution(r.data)).catch(() => {});
  }, []);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.post("/reports/executive/pdf", {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "retainproof-briefing.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setPdfLoading(false);
    }
  };

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/reports/executive");
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Report generation failed");
    } finally {
      setLoading(false);
    }
  };

  // Merge channels across the three models into one comparison table (revenue credited)
  const buildComparison = () => {
    if (!attribution) return [];
    const map = {};
    const add = (rows, key) => (rows || []).forEach((r) => {
      map[r.channel] = map[r.channel] || { channel: r.channel, first: 0, last: 0, multi: 0 };
      map[r.channel][key] = r.revenue;
    });
    add(attribution.first_touch, "first");
    add(attribution.last_touch, "last");
    add(attribution.multi_touch, "multi");
    return Object.values(map).sort((a, b) => b.multi - a.multi);
  };
  const comparison = buildComparison();

  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Plain language</p>
        <h1 className="font-display text-4xl font-light mt-1">Executive Report</h1>
        <p className="text-sm text-zinc-500 mt-2">
          A managing-partner briefing generated by AI from your live data — waste, underperforming vendors and intake
          failures, in plain English.
        </p>
      </div>

      {/* Attribution model comparison */}
      {comparison.length > 0 && (
        <div className="border border-zinc-200 bg-white mb-8" data-testid="attribution-comparison">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-zinc-100">
            <GitBranch className="h-4 w-4 text-[#174A7E]" />
            <h2 className="font-display text-xl">How attribution model changes the story</h2>
          </div>
          <p className="px-5 pt-3 text-xs text-zinc-500">
            The same signed revenue, credited three ways. Single-source models (first/last touch) over-credit one channel —
            multi-touch shows what each channel actually contributed.
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Channel</TableHead>
                <TableHead className="text-right">First-touch</TableHead>
                <TableHead className="text-right">Last-touch</TableHead>
                <TableHead className="text-right">Multi-touch</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparison.map((c) => (
                <TableRow key={c.channel} data-testid={`comparison-row-${c.channel}`}>
                  <TableCell className="font-medium">{c.channel}</TableCell>
                  <TableCell className="text-right font-mono-data text-zinc-500">{currency(c.first)}</TableCell>
                  <TableCell className="text-right font-mono-data text-zinc-500">{currency(c.last)}</TableCell>
                  <TableCell className="text-right font-mono-data text-[#156F52] font-medium">{currency(c.multi)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {!report && (
        <div className="border border-zinc-200 bg-white p-12 text-center" data-testid="report-empty">
          <FileText className="h-10 w-10 mx-auto mb-4 text-zinc-300" />
          <p className="text-zinc-500 mb-6">Generate this period's briefing from your budgets, vendors, leads and invoices.</p>
          {writable ? (
            <>
              <Button
                onClick={generate}
                disabled={loading}
                data-testid="generate-report-button"
                className="bg-[#0B1F33] text-white rounded-none h-11 px-6"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Writing briefing…</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" /> Generate executive briefing</>
                )}
              </Button>
              <Button onClick={downloadPdf} disabled={pdfLoading} variant="outline" className="rounded-none h-11 px-6 ml-2 border-zinc-300" data-testid="download-pdf-button">
                {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Download PDF
              </Button>
            </>
          ) : (
            <p className="text-sm text-zinc-500" data-testid="report-no-perm">Only Owners and Marketing Directors can generate reports.</p>
          )}
        </div>
      )}

      {report && (
        <div className="border border-zinc-200 bg-white p-10" data-testid="report-content">
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-zinc-100">
            <div>
              <h2 className="font-display text-3xl">Monthly Marketing Briefing</h2>
              <p className="font-mono-data text-xs text-zinc-500 mt-1">
                Generated {new Date(report.generated_at).toLocaleString()}
              </p>
            </div>
            <Button onClick={generate} disabled={loading} variant="outline" className="rounded-none" data-testid="regenerate-report-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            </Button>
          </div>
          <div className="prose prose-zinc max-w-none whitespace-pre-wrap leading-relaxed text-[15px] text-zinc-800">
            {report.report}
          </div>
        </div>
      )}
    </div>
  );
}
